@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_driver

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.dashboard.PasswordChangeOverlay
import org.example.project.manager.LumaticaApiManager
import org.example.project.registrierung.RegistrierungApi
import org.example.project.registrierung.TrustedDevice
import org.example.project.registrierung.TrustedDevicesApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.LoginUserApi
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val driverProfileJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

@Composable
fun DRIVERProfilAuswahl() {
    Style(DriverProfileStyles)
    val t: (String) -> String = { LocalizationManager.get(it) }

    val scope = rememberCoroutineScope()
    var user by remember { mutableStateOf(LoginService.getUser()) }
    var company by remember { mutableStateOf<All?>(null) }
    var isEditing by remember { mutableStateOf(false) }
    var emailInput by remember { mutableStateOf("") }
    var mobilInput by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var showPasswordOverlay by remember { mutableStateOf(false) }
    var trustedDevices by remember { mutableStateOf<List<TrustedDevice>>(emptyList()) }
    var trustedDevicesLoading by remember { mutableStateOf(false) }
    val userVersion = LoginUserApi.version()

    suspend fun saveUser(nextUser: All): Boolean {
        val updated = persistDriverUser(nextUser) ?: return false
        LoginService.updateUser { updated }
        user = updated
        GlobalManager.triggerSessionConfigReload()
        return true
    }

    fun startEditing(currentUser: All) {
        isEditing = true
        emailInput = currentUser.email
        mobilInput = currentUser.mobil
        message = null
        error = null
    }

    fun stopEditing(currentUser: All) {
        isEditing = false
        emailInput = currentUser.email
        mobilInput = currentUser.mobil
        error = null
    }

    suspend fun reloadTrustedDevices() {
        trustedDevicesLoading = true
        trustedDevices = TrustedDevicesApi.load("mobilefahrerregistrierungscreen")
        trustedDevicesLoading = false
    }

    LaunchedEffect(user?.firmenId) {
        val firmenId = user?.firmenId.orEmpty().trim()
        company = if (firmenId.isBlank()) {
            null
        } else {
            RegistrierungApi.loadCompanies().firstOrNull { item ->
                item.firmenId.trim() == firmenId || item.id.trim() == firmenId
            }
        }
    }

    LaunchedEffect(user?.id, user?.firmenId) {
        reloadTrustedDevices()
    }

    LaunchedEffect(user?.id, userVersion) {
        val userId = user?.id.orEmpty().trim()
        if (userId.isBlank()) return@LaunchedEffect
        val freshUser = LoginUserApi.loadUser(userId) ?: return@LaunchedEffect
        LoginService.updateUser { freshUser }
        user = freshUser
    }

    val currentUser = user
    if (currentUser == null) {
        Div({ classes(DriverProfileStyles.page) }) {
            Div({ classes(DriverProfileStyles.dataCard) }) {
                P({ classes(DriverProfileStyles.bodyText) }) { Text(t("driver_profile_no_driver")) }
            }
        }
        return
    }

    val currentCompany = company
    val driverInitials = listOf(currentUser.vorname, currentUser.nachname)
        .filter { it.isNotBlank() }
        .mapNotNull { it.firstOrNull { char -> char.isLetter() } }
        .joinToString("")
        .ifBlank { currentUser.kuerzel.take(4) }
        .uppercase()

    Div({ classes(DriverProfileStyles.page) }) {
        Div({ classes(DriverProfileStyles.profileTop) }) {
            Div({ classes(DriverProfileStyles.profileTopRow) }) {
                DriverAvatarCircle(
                    imageSrc = "",
                    offsetX = 0.0,
                    offsetY = 0.0,
                    zoom = "1",
                    initials = driverInitials
                )
                Img(src = "images/stift.svg", attrs = {
                    classes(DriverProfileStyles.headerEditIcon)
                    onClick {
                        if (!saving) {
                            if (isEditing) stopEditing(currentUser) else startEditing(currentUser)
                        }
                    }
                })
            }

            H2({ classes(DriverProfileStyles.nameTitle) }) {
                Text(listOf(currentUser.vorname, currentUser.nachname).filter { it.isNotBlank() }.joinToString(" ").ifBlank { t("driver_fallback_name") })
            }
            P({ classes(DriverProfileStyles.companyTitle) }) { Text(currentUser.firma.ifBlank { "-" }) }
        }

        Div({ classes(DriverProfileStyles.dataCard) }) {
            editableValueRow(
                label = t("driver_email_address"),
                editing = isEditing,
                inputType = InputType.Email,
                value = emailInput,
                displayValue = currentUser.email,
                placeholder = t("driver_new_email_address"),
                onValueChange = {
                    emailInput = it
                    error = null
                }
            )
            editableValueRow(
                label = t("driver_mobile"),
                editing = isEditing,
                inputType = InputType.Tel,
                value = mobilInput,
                displayValue = currentUser.mobil,
                placeholder = t("driver_new_mobile"),
                onValueChange = {
                    mobilInput = it
                    error = null
                }
            )
            passwordRow(editing = isEditing, t = t) {
                error = null
                message = null
                showPasswordOverlay = true
            }
            infoRow(t("driver_first_name"), currentUser.vorname)
            infoRow(t("driver_last_name"), currentUser.nachname)
            infoRow(t("driver_company"), currentUser.firma)
            infoRow(t("driver_postal_code"), currentCompany?.plz.orEmpty().ifBlank { currentUser.plz })
            infoRow(t("driver_street"), currentCompany?.strasse.orEmpty().ifBlank { currentUser.strasse })
            infoRow(t("driver_house_number_long"), currentCompany?.hausnummer.orEmpty().ifBlank { currentUser.hausnummer }, false)
        }

        Div({ classes(DriverProfileStyles.dataCard) }) {
            Span({ classes(DriverProfileStyles.infoLabel) }) { Text(t("driver_trusted_devices")) }
            if (trustedDevices.isEmpty()) {
                P({ classes(DriverProfileStyles.bodyText) }) {
                    Text(if (trustedDevicesLoading) t("driver_trusted_devices_loading") else t("driver_trusted_devices_empty"))
                }
            } else {
                trustedDevices.forEach { device ->
                    trustedDeviceRow(
                        device = device,
                        t = t,
                        deleting = saving,
                        onDelete = {
                            saving = true
                            scope.launch {
                                val ok = TrustedDevicesApi.delete("mobilefahrerregistrierungscreen", device.id)
                                if (ok) {
                                    message = t("driver_trusted_device_deleted")
                                    reloadTrustedDevices()
                                } else {
                                    error = t("driver_trusted_device_delete_failed")
                                }
                                saving = false
                            }
                        }
                    )
                }
            }
        }

        if (isEditing) {
            Div({ classes(DriverProfileStyles.editActionBar) }) {
                LumaticaButton(
                    label = t("zurück"),
                    onClick = { stopEditing(currentUser) },
                    variant = LumaticaButtonVariant.Standard,
                    enabled = !saving,
                    heightPx = 42
                )
                LumaticaButton(
                    label = if (saving) t("driver_saving") else t("driver_save"),
                    onClick = {
                        val normalizedEmail = emailInput.trim()
                        val normalizedMobil = mobilInput.trim()
                        when {
                            normalizedEmail.isBlank() || !normalizedEmail.contains("@") -> {
                                error = t("driver_error_invalid_email")
                            }
                            normalizedMobil.isBlank() -> {
                                error = t("driver_error_mobile_required")
                            }
                            else -> {
                                scope.launch {
                                    saving = true
                                    error = null
                                    message = null
                                    val ok = saveUser(
                                        currentUser.copy(
                                            email = normalizedEmail,
                                            mobil = normalizedMobil
                                        )
                                    )
                                    if (ok) {
                                        isEditing = false
                                        message = t("driver_profile_saved")
                                    } else {
                                        error = t("driver_profile_save_failed")
                                    }
                                    saving = false
                                }
                            }
                        }
                    },
                    variant = LumaticaButtonVariant.Action,
                    enabled = !saving,
                    heightPx = 42
                )
            }
        }

        if (!message.isNullOrBlank()) {
            Div({ classes(DriverProfileStyles.statusWrap) }) {
                Span({ classes(DriverProfileStyles.successText) }) { Text(message!!) }
            }
        }
        if (!error.isNullOrBlank()) {
            Div({ classes(DriverProfileStyles.statusWrap) }) {
                Span({ classes(DriverProfileStyles.errorText) }) { Text(error!!) }
            }
        }
    }

    if (showPasswordOverlay) {
        PasswordChangeOverlay(
            art = "driver",
            onClose = {
                showPasswordOverlay = false
                user = LoginService.getUser()
            }
        )
    }
}

@Composable
private fun infoRow(label: String, value: String, withDivider: Boolean = true) {
    Div({
        classes(DriverProfileStyles.infoRow)
        if (withDivider) classes(DriverProfileStyles.rowDivider)
    }) {
        Span({ classes(DriverProfileStyles.infoLabel) }) { Text(label) }
        Span({ classes(DriverProfileStyles.infoValue) }) { Text(value.ifBlank { "-" }) }
    }
}

@Composable
private fun editableValueRow(
    label: String,
    editing: Boolean,
    inputType: InputType<String>,
    value: String,
    displayValue: String,
    placeholder: String,
    onValueChange: (String) -> Unit
) {
    Div({ classes(DriverProfileStyles.infoRow, DriverProfileStyles.rowDivider, DriverProfileStyles.editableRow) }) {
        Span({ classes(DriverProfileStyles.infoLabel) }) { Text(label) }
        if (editing) {
            Input(inputType, attrs = {
                classes(DriverProfileStyles.inlineInput)
                value(value)
                attr("placeholder", placeholder)
                onInput { onValueChange(it.value) }
            })
        } else {
            Span({ classes(DriverProfileStyles.infoValue) }) { Text(displayValue.ifBlank { "-" }) }
        }
    }
}

@Composable
private fun trustedDeviceRow(device: TrustedDevice, t: (String) -> String, deleting: Boolean, onDelete: () -> Unit) {
    Div({
        classes(DriverProfileStyles.infoRow, DriverProfileStyles.rowDivider)
        style {
            alignItems(AlignItems.Center)
            gap(12.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(3.px)
                flex(1)
                minWidth(0.px)
            }
        }) {
            Span({ classes(DriverProfileStyles.infoLabel) }) {
                Text(device.label.ifBlank { if (device.mode == "mobile") "Driver Mobile" else "NET Desktop" } + if (device.current) " (${t("driver_trusted_device_current")})" else "")
            }
            Span({ classes(DriverProfileStyles.infoValue) }) {
                Text(device.clientIp.ifBlank { "-" })
            }
        }
        LumaticaButton(
            label = t("driver_trusted_device_delete"),
            onClick = onDelete,
            variant = LumaticaButtonVariant.Standard,
            enabled = !deleting,
            heightPx = 34
        )
    }
}

@Composable
private fun passwordRow(editing: Boolean, t: (String) -> String, onOpen: () -> Unit) {
    Div({ classes(DriverProfileStyles.infoRow, DriverProfileStyles.rowDivider) }) {
        Span({ classes(DriverProfileStyles.infoLabel) }) { Text(t("driver_password")) }
        if (editing) {
            Span({
                classes(DriverProfileStyles.passwordAction)
                onClick { onOpen() }
            }) { Text(t("driver_change_password")) }
        } else {
            Span({ classes(DriverProfileStyles.infoValue) }) { Text("••••••••") }
        }
    }
}

private suspend fun persistDriverUser(user: All): All? {
    val firmenId = user.firmenId.trim()
    if (firmenId.isBlank()) return LoginUserApi.saveUser(user)

    val saveData = user.copy(
        firmenId = firmenId,
        firma = user.firma.ifBlank { LoginService.getUser()?.firma.orEmpty() },
        kunde = user.kunde.ifBlank { user.firma.ifBlank { LoginService.getUser()?.firma.orEmpty() } }
    )
    // Die Unterschrift wird separat als signatur.json verwaltet. Sie gehoert weder
    // zum Profilfoto noch in die Nutzlast einer normalen Profilaenderung.
    val body = driverProfileJson.encodeToString(
        All.AllNames.serializer(),
        saveData.copy(signatur = "")
    )
    val raw = runCatching {
        LumaticaApiManager.postJson(
            "userverwaltung/company-save-json/${apiPart(firmenId)}/${apiPart(user.id)}",
            body
        )
    }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    return runCatching { driverProfileJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private fun apiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

object DriverProfileStyles : StyleSheet() {
    val page by style {
        minHeight(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(18.px)
        padding(24.px, 16.px, 36.px, 16.px)
        boxSizing("border-box")
    }

    val profileTop by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        gap(10.px)
        width(100.percent)
        maxWidth(520.px)
    }

    val profileTopRow by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.FlexStart)
        gap(16.px)
    }

    val avatarButton by style {
        width(128.px)
        height(128.px)
        borderRadius(50.percent)
        overflow("hidden")
        position(Position.Relative)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)")
        property("border", "1px solid rgba(255,255,255,0.16)")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        cursor("pointer")
        property("box-shadow", "0 18px 38px rgba(0,0,0,0.24)")
        property("flex-shrink", "0")
    }

    val avatarImage by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        maxWidth(100.percent)
        maxHeight(100.percent)
        property("width", "auto")
        property("height", "auto")
        property("object-fit", "contain")
        property("transform-origin", "center center")
        property("will-change", "transform")
    }

    val avatarPlaceholder by style {
        width(52.px)
        height(52.px)
        opacity(0.9)
        property("filter", "brightness(0) invert(1)")
    }

    val headerEditIcon by style {
        width(18.px)
        height(18.px)
        cursor("pointer")
        marginTop(8.px)
        opacity(0.9)
        property("flex-shrink", "0")
    }

    val nameTitle by style {
        margin(0.px)
        color(Color.white)
        fontSize(28.px)
        textAlign("center")
    }

    val companyTitle by style {
        margin(0.px)
        color(Color("#b9c4d4"))
        fontSize(15.px)
        textAlign("center")
    }

    val dataCard by style {
        width(100.percent)
        maxWidth(520.px)
        padding(8.px, 18.px)
        borderRadius(24.px)
        property("background", "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("box-shadow", "0 16px 40px rgba(0,0,0,0.18)")
        boxSizing("border-box")
    }

    val infoRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(12.px)
        padding(14.px, 0.px)
    }

    val editableRow by style {
        alignItems(AlignItems.Center)
    }

    val rowDivider by style {
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
    }

    val infoLabel by style {
        color(Color("#97a7bc"))
        fontSize(12.px)
        fontWeight("600")
    }

    val infoValue by style {
        color(Color.white)
        fontSize(14.px)
        textAlign("right")
        property("word-break", "break-word")
    }

    val inlineInput by style {
        width(220.px)
        maxWidth(65.percent)
        padding(10.px, 12.px)
        borderRadius(12.px)
        color(Color.white)
        backgroundColor(Color.transparent)
        property("background", "rgba(255,255,255,0.04)")
        property("border", "1px solid rgba(255,255,255,0.16)")
        property("box-sizing", "border-box")
        textAlign("right")
    }

    val passwordAction by style {
        color(Color.white)
        fontSize(13.px)
        textDecoration("underline")
        cursor("pointer")
    }

    val editActionBar by style {
        width(100.percent)
        maxWidth(520.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(10.px)
    }

    val bodyText by style {
        margin(0.px)
        color(Color("#c9d1df"))
        fontSize(14.px)
        lineHeight("1.5")
        textAlign("center")
    }

    val statusWrap by style {
        width(100.percent)
        maxWidth(520.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
    }

    val errorText by style {
        color(Color("#ff9b9b"))
        fontSize(13.px)
    }

    val successText by style {
        color(Color("#9be7b2"))
        fontSize(13.px)
    }

    val overlayBackdrop by style {
        position(Position.Fixed)
        property("inset", "0")
        backgroundColor(rgba(0, 0, 0, 0.72))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        padding(18.px)
        property("z-index", "120000")
        boxSizing("border-box")
    }

    val avatarOverlayCard by style {
        width(100.percent)
        maxWidth(720.px)
        maxHeight(88.vh)
        overflowY("auto")
        padding(22.px)
        borderRadius(22.px)
        property("background", "linear-gradient(180deg, rgba(17,21,29,0.98) 0%, rgba(23,28,38,0.98) 100%)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(16.px)
        boxSizing("border-box")
    }

    val overlayTitle by style {
        margin(0.px)
        color(Color.white)
        fontSize(24.px)
    }

    val avatarPreviewWrap by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
    }

    val avatarPreviewCircle by style {
        width(196.px)
        height(196.px)
        overflow("hidden")
        position(Position.Relative)
        property("background", "rgba(255,255,255,0.04)")
        property("border", "1px solid rgba(255,255,255,0.14)")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        borderRadius(50.percent)
        property("box-shadow", "0 12px 28px rgba(0,0,0,0.28)")
    }

    val avatarPreviewImageStage by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        property("width", "auto")
        property("height", "auto")
        maxWidth(100.percent)
        maxHeight(100.percent)
        property("object-fit", "contain")
        property("transform-origin", "center center")
        property("will-change", "transform")
    }

    val avatarPreviewPlaceholder by style {
        width(74.px)
        height(74.px)
        opacity(0.9)
        property("filter", "brightness(0) invert(1)")
    }

    val sliderGroup by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
    }

    val sliderLabel by style {
        color(Color("#d7e1ef"))
        fontSize(13.px)
        fontWeight("600")
    }

    val sliderHint by style {
        color(Color("#9aa8bc"))
        fontSize(12.px)
    }

    val rangeInput by style {
        width(100.percent)
    }

    val galleryHeaderRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(12.px)
        flexWrap(FlexWrap.Wrap)
    }

    val galleryActionRow by style {
        display(DisplayStyle.Flex)
        gap(8.px)
        flexWrap(FlexWrap.Wrap)
    }

    val galleryTitle by style {
        color(Color.white)
        fontSize(16.px)
        fontWeight("700")
    }

    val galleryEmptyText by style {
        margin(0.px)
        color(Color("#9aa8bc"))
        fontSize(13.px)
    }

    val galleryGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(110px, 1fr))")
        gap(10.px)
    }

    val galleryScroller by style {
        maxHeight(34.vh)
        overflowY("auto")
        paddingRight(4.px)
    }

    val galleryItem by style {
        height(118.px)
        borderRadius(14.px)
        property("border", "1px solid rgba(255,255,255,0.10)")
        position(Position.Relative)
        overflow("hidden")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.FlexEnd)
        justifyContent(JustifyContent.Center)
        padding(0.px, 0.px, 8.px, 0.px)
        boxSizing("border-box")
        cursor("pointer")
        property("background", "rgba(255,255,255,0.03)")
    }

    val galleryItemSelected by style {
        property("border", "2px solid rgba(143,210,255,0.95)")
        property("box-shadow", "0 0 0 1px rgba(143,210,255,0.28), 0 14px 30px rgba(0,0,0,0.35)")
    }

    val galleryThumbImage by style {
        position(Position.Absolute)
        left(0.px)
        top(0.px)
        width(100.percent)
        height(100.percent)
        property("object-fit", "contain")
        property("background", "rgba(9,12,18,0.55)")
    }

    val galleryBadge by style {
        color(Color("#dbe7ff"))
        fontSize(11.px)
        backgroundColor(Color("rgba(0,0,0,0.45)"))
        padding(3.px, 8.px)
        borderRadius(999.px)
    }

    val overlayActionRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(10.px)
        flexWrap(FlexWrap.Wrap)
    }
}
